import type {
  BatchCarryover,
  BatchRun,
  BatchRetryScope,
  NeedsAttention,
  ReasonCount,
} from '../../types/batch-retry.api.types';
import type { BatchProcessingColumn } from '../../types/catalog-operations.types';

export const SCOPE_LABEL: Record<BatchRetryScope, string> = {
  REMAINING: 'All remaining',
  FAILED: 'Failed only',
  NOT_ATTEMPTED: 'Not attempted only',
};

/** "PROVIDER_ERROR" -> "Provider error". */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString();
}

export function reasonsToBars(reasons: ReasonCount[]) {
  return reasons.map((r) => ({
    key: r.reason,
    label: r.label || humanize(r.reason),
    count: r.count,
  }));
}

/** Merge reason lists (same reason summed), biggest first. */
export function mergeReasons(lists: ReasonCount[][]): ReasonCount[] {
  const byReason = new Map<string, ReasonCount>();
  for (const r of lists.flat()) {
    const prev = byReason.get(r.reason);
    byReason.set(r.reason, prev ? { ...prev, count: prev.count + r.count } : { ...r });
  }
  return [...byReason.values()].sort((a, b) => b.count - a.count);
}

/** Per-class figures derived from the classified buckets. */
export function summarizeNeedsAttention(na: NeedsAttention | undefined) {
  const buckets = na?.buckets ?? [];
  const retryable = buckets.find((b) => b.code === 'ENRICHMENT_FAILED_RETRYABLE');
  const permanent = buckets.find((b) => b.code === 'ENRICHMENT_FAILED_PERMANENT');
  const notAttempted = buckets.find((b) => b.code === 'NOT_ATTEMPTED');
  const failedBuckets = [retryable, permanent].filter((b) => b !== undefined);
  return {
    failed: (retryable?.count ?? 0) + (permanent?.count ?? 0),
    failedRetryable: retryable?.count ?? 0,
    failedPermanent: permanent?.count ?? 0,
    notAttempted: notAttempted?.count ?? 0,
    failedReasons: mergeReasons(failedBuckets.map((b) => b.reasons)),
  };
}

/** "Showing 51–100 of 3,097" for a zero-based page. */
export function pageRange(page: number, size: number, total: number): string {
  if (total <= 0) return 'No products';
  const from = page * size + 1;
  const to = Math.min(total, (page + 1) * size);
  return `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`;
}

/**
 * A run's denominator for progress. A running run measures against what it set out to cover
 * (planned); a finished one against what it actually picked, so its bar fills completely.
 */
export function runTotal(run: {
  status: string | null;
  planned: number | null;
  picked: number | null;
}): number {
  if (run.status === 'RUNNING') return run.planned ?? run.picked ?? 0;
  return run.picked ?? run.planned ?? 0;
}

/**
 * The products filter (`?cls=`) that shows what a run left: its failures, the products it never
 * reached, or — when it left both — every product of the run (`undefined`).
 */
export function leftOverFilter(run: BatchRun): 'FAILED' | 'NOT_ATTEMPTED' | undefined {
  const failed = run.failed ?? 0;
  const notAttempted = run.notAttempted ?? 0;
  if (failed > 0 && notAttempted === 0) return 'FAILED';
  if (notAttempted > 0 && failed === 0) return 'NOT_ATTEMPTED';
  return undefined;
}

/** The part of "All Products · 28 Oct" before the date; names without a " · " have no date part. */
export function splitBatchName(name: string): { title: string; date: string | null } {
  const at = name.lastIndexOf(' · ');
  return at < 0
    ? { title: name, date: null }
    : { title: name.slice(0, at), date: name.slice(at + 3) };
}

/** "All Products · 28 Oct" — the name already carries its date; add it when the name does not. */
export function batchLabel(ref: { name: string; createdAt: string | null }): string {
  if (splitBatchName(ref.name).date || !ref.createdAt) return ref.name;
  return `${ref.name} · ${formatShortDate(ref.createdAt)}`;
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** Whether a carry-over read has anything worth showing (either direction). */
export function carryoverTotals(carryover: BatchCarryover | null | undefined) {
  return {
    carriedIn: carryover?.carriedIn?.total ?? 0,
    movedOut: carryover?.movedOut?.total ?? 0,
  };
}

/**
 * The Enriched stage card's link: where this batch's products came from, or went to. One batch
 * is named; several are counted ("from 3 earlier batches").
 */
export function carryoverNoteText(carryover: BatchCarryover | null | undefined): string | null {
  const parts: string[] = [];
  const carriedIn = carryover?.carriedIn;
  if (carriedIn && carriedIn.total > 0) {
    const sources = carriedIn.bySource;
    const from =
      sources.length === 1 && sources[0]
        ? batchLabel(sources[0])
        : `${String(sources.length)} earlier batches`;
    parts.push(
      `${formatCount(carriedIn.enriched)} of ${formatCount(carriedIn.total)} from ${from}`,
    );
  }
  const movedOut = carryover?.movedOut;
  if (movedOut && movedOut.total > 0) {
    const batches = movedOut.byBatch;
    const to =
      batches.length === 1 && batches[0]
        ? batchLabel(batches[0])
        : `${String(batches.length)} later batches`;
    parts.push(`${formatCount(movedOut.total)} moved to ${to}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * The Enriched stage card's one-line carry-over link: "26 from earlier · 26 moved". Counts only —
 * which batches, and how many were enriched, live in Run details → Runs (and the full sentence,
 * {@link carryoverNoteText}, is the link's tooltip).
 */
export function carryoverNoteShort(carryover: BatchCarryover | null | undefined): string | null {
  const parts: string[] = [];
  const carriedIn = carryover?.carriedIn;
  if (carriedIn && carriedIn.total > 0) {
    parts.push(`${formatCount(carriedIn.total)} from earlier`);
  }
  const movedOut = carryover?.movedOut;
  if (movedOut && movedOut.total > 0) {
    parts.push(`${formatCount(movedOut.total)} moved`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** "345 curated = 319 enriched + 0 failed + … + 26 moved": every product is in exactly one bucket. */
export function balanceLine(balance: BatchCarryover['balance']): string {
  const lead =
    balance.carriedIn > 0
      ? `${formatCount(balance.curated)} curated + ${formatCount(balance.carriedIn)} from earlier batches = ${formatCount(balance.curated + balance.carriedIn)}`
      : `${formatCount(balance.curated)} curated`;
  return `${lead} = ${formatCount(balance.enriched)} enriched + ${formatCount(balance.failed)} failed + ${formatCount(balance.skipped)} skipped + ${formatCount(balance.notReached)} not reached + ${formatCount(balance.excluded)} excluded + ${formatCount(balance.moved)} moved`;
}

export const PROCESSING_FROM_EARLIER = 'From earlier batches';
export const PROCESSING_MOVED_TO_LATER = 'Moved to later batches';

/**
 * Processing Summary: the Enriched column keeps its rows and gains "From earlier batches" /
 * "Moved to later batches" (only when > 0), each opening the Runs section that explains them.
 */
export function withCarryoverRows(
  columns: BatchProcessingColumn[],
  carryover: BatchCarryover | null | undefined,
): BatchProcessingColumn[] {
  const { carriedIn, movedOut } = carryoverTotals(carryover);
  if (carriedIn === 0 && movedOut === 0) return columns;
  return columns.map((col) =>
    col.title === 'Enriched'
      ? {
          ...col,
          rows: [
            ...col.rows,
            ...(carriedIn > 0
              ? [{ label: PROCESSING_FROM_EARLIER, value: formatCount(carriedIn), drillIn: true }]
              : []),
            ...(movedOut > 0
              ? [{ label: PROCESSING_MOVED_TO_LATER, value: formatCount(movedOut), drillIn: true }]
              : []),
          ],
        }
      : col,
  );
}
